import React from "react";
import PolicyLayout from "./PolicyLayout";
import "../css/policy.scss";

const EFFECTIVE_DATE = "Effective 16 October 2026";
const SUPPORT_EMAIL = "service@nartvmora.site";

const policies = {
  hub: {
    eyebrow: "Customer care & legal",
    title: "Policies & Information",
    intro:
      "A single place to find the terms, service standards and privacy information that apply when you shop with nartvmora.",
    updated: "Reviewed 16 October 2026",
    sections: [
      {
        id: "shopping-policies",
        title: "Shopping policies",
        body: [
          "These pages explain how orders, payments, delivery, returns, promotions and product availability are handled.",
        ],
        links: [
          { to: "/orders", label: "Orders & cancellations" },
          { to: "/payments", label: "Payments & billing" },
          { to: "/shipping", label: "Shipping & delivery" },
          { to: "/returns", label: "Returns & refunds" },
          { to: "/sale-policy", label: "Sale & promotions" },
          { to: "/pre-orders", label: "Pre-orders" },
          { to: "/pricing-availability", label: "Pricing & availability" },
          { to: "/consumer-guarantees", label: "Consumer guarantees" },
        ],
      },
      {
        id: "privacy-policies",
        title: "Privacy and browser data",
        body: [
          "Learn what information may be handled, why it is needed and how to exercise privacy choices.",
        ],
        links: [
          { to: "/privacy", label: "Privacy notice" },
          { to: "/privacy-choices", label: "Your privacy choices" },
          { to: "/cookies", label: "Cookie policy" },
        ],
      },
      {
        id: "site-policies",
        title: "Website and legal policies",
        body: [
          "These documents cover website use, accessibility, original content and reports about potentially infringing material.",
        ],
        links: [
          { to: "/terms", label: "Terms of use" },
          { to: "/acceptable-use", label: "Acceptable use" },
          { to: "/accessibility", label: "Accessibility statement" },
          { to: "/intellectual-property", label: "Intellectual property & takedown" },
        ],
      },
      {
        id: "policy-contact",
        title: "Questions about a policy",
        body: [
          `Email ${SUPPORT_EMAIL} and identify the policy or order concerned. Do not send full payment-card details, passwords or unnecessary identity documents by email.`,
        ],
        links: [{ to: "/contact", label: "Contact customer care" }],
      },
    ],
  },

  orders: {
    title: "Orders & Cancellations",
    intro:
      "When an order becomes binding, when it can be changed and what happens if we cannot fulfil it.",
    sections: [
      {
        id: "placing-an-order",
        title: "Placing an order",
        body: [
          "Review the product, size, colour, delivery address and total before paying. Submitting payment is an offer to purchase the items in the bag under the policies displayed at that time.",
          "An on-screen message or automated email acknowledging the request does not by itself mean that the order has been accepted.",
        ],
      },
      {
        id: "acceptance",
        title: "Order acceptance",
        body: [
          "An order is accepted after payment is authorised, stock and order details are checked, and nartvmora issues an order confirmation. If those checks cannot be completed, any payment already captured will be returned to the original payment method.",
          "Separate items may be accepted or dispatched at different times. A contract for each item is formed only when that item is confirmed, subject to any mandatory rule that applies to the customer.",
        ],
      },
      {
        id: "changes",
        title: "Changing an order",
        body: [
          `Contact ${SUPPORT_EMAIL} promptly with the order reference. We may be able to correct contact details, a delivery address or a variant before fulfilment starts, but changes are not guaranteed.`,
          "For security, we cannot change the payment method or increase the order value after payment. A replacement order may be required.",
        ],
      },
      {
        id: "customer-cancellation",
        title: "Customer cancellation",
        body: [
          "A cancellation request can be made before dispatch. If fulfilment has already started or the parcel has left the warehouse, use the returns process after delivery instead.",
          "Any statutory cancellation or cooling-off right available in the customer's location remains unaffected. Where such a right applies, the required notice period, return condition and permitted deductions will be honoured.",
        ],
        links: [{ to: "/returns", label: "Returns & refunds" }],
      },
      {
        id: "merchant-cancellation",
        title: "When we may cancel or limit an order",
        items: [
          "An item is unavailable or cannot pass a final quality check.",
          "A price, discount, product description or delivery option contains an obvious error.",
          "Payment is not authorised, is reversed or fails verification.",
          "The order appears fraudulent, unlawful, intended for unauthorised resale or exceeds a reasonable purchase limit.",
          "A carrier, sanctions rule or other legal restriction prevents fulfilment to the destination.",
        ],
      },
      {
        id: "records-help",
        title: "Order records and help",
        body: [
          "Keep the order confirmation and delivery tracking until the order and any return are complete. If a confirmation does not arrive, check the email address used at checkout and the spam folder before contacting customer care.",
        ],
        links: [
          { to: "/payments", label: "Payments & billing" },
          { to: "/contact", label: "Get order help" },
        ],
      },
    ],
  },

  payments: {
    title: "Payments & Billing",
    intro:
      "How prices, payment authorisation, refunds and payment information are handled.",
    sections: [
      {
        id: "currency-methods",
        title: "Currency and available methods",
        body: [
          "Store prices and checkout totals are shown in United States dollars (USD). The payment page displays the payment methods currently available for the customer's device, billing location and order.",
          "A bank or card issuer may apply its own foreign-exchange rate, international transaction fee or other charge. Those charges are not collected or controlled by nartvmora.",
        ],
      },
      {
        id: "payment-processing",
        title: "Payment processing",
        body: [
          "Checkout redirects to a hosted payment page operated by an independent payment processor. Card numbers, security codes and payment-account credentials are entered with that provider and are not stored in the nartvmora storefront.",
          "The processor receives transaction details needed to request payment, such as the order reference, amount, currency and product description. It handles payment data under its own terms and privacy notice.",
        ],
        links: [{ to: "/privacy", label: "Privacy notice" }],
      },
      {
        id: "authorisation",
        title: "Authorisation and confirmation",
        body: [
          "A payment attempt may be declined or held for authentication by the issuer or payment provider. Do not submit repeated attempts if the result is unclear; first check the payment account and contact customer care to reduce the risk of duplicate authorisations.",
          "An order is not confirmed until the payment result is successful and the order passes the checks described in the Orders & Cancellations policy.",
        ],
      },
      {
        id: "taxes-charges",
        title: "Taxes, delivery and import charges",
        body: [
          "The bag and checkout identify the item subtotal, standard delivery charge and transaction total. Any tax collected by nartvmora will be included in or separately identified in the total before payment.",
          "Import duties, destination taxes, brokerage or carrier charges may still be payable by the recipient where checkout does not expressly state that they have been collected.",
        ],
        links: [{ to: "/shipping", label: "Shipping & delivery" }],
      },
      {
        id: "refunds",
        title: "Refunds and reversals",
        body: [
          "Approved refunds are submitted to the original payment method in the original transaction currency. The payment provider or issuer controls when the credit appears, and currency conversion may cause the statement amount to differ from the amount originally debited.",
          "If the original method is unavailable, contact customer care. We will not redirect a refund to an unrelated account without appropriate verification.",
        ],
      },
      {
        id: "payment-disputes",
        title: "Unrecognised or disputed payments",
        body: [
          `Email ${SUPPORT_EMAIL} promptly with the order reference and the non-sensitive details of the issue. Never send a full card number or security code. Customers retain any right to contact their payment provider.`,
        ],
        links: [{ to: "/contact", label: "Contact customer care" }],
      },
    ],
  },

  sale: {
    title: "Sale & Promotions Policy",
    intro:
      "Rules for reduced-price items, discount codes and limited-time offers.",
    sections: [
      {
        id: "sale-pricing",
        title: "Sale pricing",
        body: [
          "A reduced item may show its current price beside a comparison price. The comparison price is the price identified for that product before the reduction; it is not a promise that every colour, size or market was offered at that price for the same period.",
          "Sale pricing applies only while displayed and while stock lasts. Placing an item in the bag does not reserve it or preserve a price after an offer ends.",
        ],
      },
      {
        id: "promotions",
        title: "Promotion terms",
        items: [
          "A promotion runs only for the period, products, destinations and customers stated with the offer.",
          "Unless the offer says otherwise, one discount code may be used per order and discounts cannot be combined, exchanged for cash or applied to an earlier order.",
          "Minimum-spend thresholds are calculated after excluded items and other discounts, and before delivery charges, duties or taxes unless the offer states otherwise.",
          "Returns may reduce the retained order below a promotion threshold; any refund will be recalculated using the value actually paid for the returned item and the terms of the offer.",
          "Codes obtained through error, manipulation, unauthorised publication or misuse may be refused or cancelled to the extent permitted by law.",
        ],
      },
      {
        id: "final-sale",
        title: "Final Sale items",
        body: [
          "An item labelled FINAL SALE before purchase is not eligible for a change-of-mind return. This restriction does not apply where the item is faulty, misdescribed, incorrect, damaged in transit or a remedy is otherwise required by applicable consumer law.",
          "If an item is merely discounted and is not labelled FINAL SALE, the standard Returns & Refunds policy applies unless a clearly disclosed promotion term provides a more favourable option.",
        ],
        links: [
          { to: "/returns", label: "Returns & refunds" },
          { to: "/consumer-guarantees", label: "Consumer guarantees" },
        ],
      },
      {
        id: "offer-changes",
        title: "Corrections and offer changes",
        body: [
          "We may correct an obvious offer error or end an offer early where fraud, a technical failure or an event outside reasonable control makes the stated promotion impracticable. An accepted order will not be repriced after purchase without the customer's agreement, except where the law permits cancellation for an obvious error.",
        ],
      },
      {
        id: "sale-help",
        title: "Promotion questions",
        body: [
          `Send the wording or screenshot of the offer and the items concerned to ${SUPPORT_EMAIL} before ordering if a discount does not apply as expected.`,
        ],
      },
    ],
  },

  preorders: {
    title: "Pre-Order Policy",
    intro:
      "What applies if a product is offered before it is ready to dispatch.",
    sections: [
      {
        id: "availability",
        title: "When pre-orders are available",
        body: [
          "The current catalogue may not always include pre-order products. When pre-orders are offered, the product page will clearly identify the item as a pre-order and show an estimated dispatch window before checkout.",
        ],
      },
      {
        id: "payment",
        title: "Payment and acceptance",
        body: [
          "Unless the product page states otherwise, payment is requested when the pre-order is placed. Acceptance remains subject to payment authorisation and the order checks in our Orders & Cancellations policy.",
        ],
        links: [
          { to: "/orders", label: "Orders & cancellations" },
          { to: "/payments", label: "Payments & billing" },
        ],
      },
      {
        id: "estimates",
        title: "Estimated dispatch dates",
        body: [
          "Pre-order dates are good-faith estimates rather than guaranteed delivery dates. Production, quality review, customs or transport issues can change the estimate. If a material delay occurs, we will provide an updated estimate and any cancellation option required by applicable law.",
        ],
      },
      {
        id: "mixed-orders",
        title: "Orders containing available items",
        body: [
          "Available and pre-order items in the same order may be held and dispatched together or sent separately. The product page or checkout will identify the approach where possible. Splitting an order after purchase cannot be guaranteed.",
        ],
      },
      {
        id: "cancellation-returns",
        title: "Cancellation and returns",
        body: [
          "Request a pre-order cancellation as early as possible. If the item has entered fulfilment, the standard return process may need to be used after delivery. Pre-order status does not remove mandatory cancellation, refund or consumer rights.",
        ],
        links: [{ to: "/returns", label: "Returns & refunds" }],
      },
    ],
  },

  pricing: {
    title: "Pricing, Product Information & Availability",
    intro:
      "How we present product details, prices, inventory and corrections.",
    sections: [
      {
        id: "product-details",
        title: "Product descriptions",
        body: [
          "We aim to describe materials, fit, measurements, colour and care accurately. Measurements are approximate, screens render colour differently, and washed denim can have natural shade and finish variation between pieces.",
          "Images are illustrative and props or styling pieces are not included unless the product description says they are part of the item.",
        ],
      },
      {
        id: "pricing",
        title: "Prices and currency",
        body: [
          "Prices are shown in USD and may change without notice before an order is placed. Delivery and any taxes collected by nartvmora are shown in the bag or checkout before payment.",
          "A price shown in a cached page, search result, advertisement or third-party service may be out of date; the checkout price controls unless it is an obvious error.",
        ],
      },
      {
        id: "availability",
        title: "Stock and reservation",
        body: [
          "Stock indicators are estimates and may change while multiple customers shop. Adding an item to a wishlist or bag does not reserve it. Stock is allocated only after an order is accepted.",
          "We may set reasonable quantity limits to protect stock availability and prevent unauthorised resale.",
        ],
      },
      {
        id: "errors",
        title: "Errors and corrections",
        body: [
          "If we discover an obvious pricing, stock or product-information error before acceptance, we may ask the customer to confirm the corrected order or cancel it and refund any captured payment. If the error materially affects an accepted order, we will offer the remedy required by applicable law.",
        ],
      },
      {
        id: "care-fit",
        title: "Fit and care",
        body: [
          "Use the product-specific fit notes and care label. Advice on the website is general and does not replace the sewn-in instructions for a particular garment.",
        ],
        links: [
          { to: "/fit-guide", label: "Fit guide" },
          { to: "/consumer-guarantees", label: "Consumer guarantees" },
        ],
      },
    ],
  },

  guarantees: {
    title: "Consumer Guarantees & Product Claims",
    intro:
      "How to report a faulty, damaged, incorrect or misdescribed product and the remedies that may be available.",
    sections: [
      {
        id: "mandatory-rights",
        title: "Your mandatory rights",
        body: [
          "Products will be supplied with the guarantees and remedies that cannot be excluded under the consumer law applying to the purchase. Nothing in this policy limits those rights or requires a customer to accept a store credit where the law requires another remedy.",
          "Change-of-mind rules are separate from rights concerning faulty, unsafe, damaged, incorrect or materially misdescribed goods.",
        ],
      },
      {
        id: "claim-types",
        title: "What may support a product claim",
        items: [
          "A manufacturing defect in fabric, hardware, stitching or construction.",
          "An item that is materially different from its description or the item ordered.",
          "Damage that was present on delivery or occurred in transit.",
          "A product that does not meet a mandatory standard or guarantee applicable to the sale.",
        ],
      },
      {
        id: "not-defect",
        title: "What is not normally a manufacturing defect",
        items: [
          "Expected fading, creasing or character that develops through normal denim wear.",
          "Damage caused by accident, misuse, alteration, incorrect sizing or failure to follow the care label.",
          "Ordinary wear and tear consistent with the item's age, use and construction.",
          "A disclosed variation or imperfection that was specifically accepted before purchase.",
        ],
      },
      {
        id: "making-claim",
        title: "How to make a claim",
        body: [
          `Email ${SUPPORT_EMAIL} with the order reference, a clear description, photographs of the whole item and the issue, and the date the issue was first noticed. Do not discard the item unless we confirm it is unnecessary for assessment.`,
          "We may request a reasonable opportunity to inspect the item. Proof of purchase can include an order confirmation, receipt or another reliable transaction record.",
        ],
        links: [{ to: "/contact", label: "Report a product issue" }],
      },
      {
        id: "remedies",
        title: "Assessment and remedies",
        body: [
          "The available remedy depends on the nature of the issue, the time since delivery and applicable law. It may include repair, replacement, refund, price reduction or reimbursement of reasonable return costs.",
          "We will not charge a customer for return delivery where the item is confirmed faulty, incorrect or otherwise qualifies for a remedy that includes return costs.",
        ],
      },
    ],
  },

  privacyChoices: {
    eyebrow: "Privacy",
    title: "Your Privacy Choices",
    intro:
      "How to ask about personal information, exercise a privacy right or manage browser data.",
    sections: [
      {
        id: "available-rights",
        title: "Rights that may be available",
        body: [
          "Depending on where a person lives and the circumstances, privacy law may provide rights to know or access personal information, correct it, delete it, restrict or object to certain processing, obtain a portable copy, withdraw consent, or appeal a decision on a request.",
          "A person may also have the right to complain to a local privacy or data-protection authority. These rights can have exceptions, including where records must be retained for legal, fraud-prevention or dispute purposes.",
        ],
      },
      {
        id: "request",
        title: "Submit a request",
        body: [
          `Email ${SUPPORT_EMAIL} with the subject “Privacy Request”. Describe the request, the country or region concerned, and the email address used with nartvmora. Do not send identity documents until they are specifically requested through an appropriate method.`,
          "We will acknowledge the request and respond within the period required by applicable law. If more time is legally permitted and reasonably needed, we will explain the extension.",
        ],
        links: [{ to: "/contact", label: "Contact privacy support" }],
      },
      {
        id: "verification",
        title: "Identity verification and authorised agents",
        body: [
          "We may compare the request with information already associated with the order or ask for limited additional information. Verification will be proportionate to the request and the sensitivity of the data.",
          "Where local law permits an authorised agent to submit a request, we may ask for proof of the agent's authority and may confirm the request directly with the individual.",
        ],
      },
      {
        id: "sale-sharing",
        title: "Sale or targeted-advertising sharing",
        body: [
          "The current storefront does not intentionally sell personal information or use it for cross-context behavioural advertising. If that practice changes, this page and any legally required opt-out method will be updated before the change applies.",
        ],
      },
      {
        id: "browser-controls",
        title: "Browser and device controls",
        body: [
          "Cart and recent-order information stored locally can be removed through the browser's site-data settings. Payment providers and other external services may offer their own privacy controls.",
        ],
        links: [
          { to: "/cookies", label: "Cookie policy" },
          { to: "/privacy", label: "Privacy notice" },
        ],
      },
      {
        id: "non-discrimination",
        title: "No unlawful discrimination",
        body: [
          "We will not unlawfully discriminate against a person for exercising a privacy right. A request may affect a service only where the requested information is genuinely necessary to provide it or retain required records.",
        ],
      },
    ],
  },

  acceptableUse: {
    eyebrow: "Legal",
    title: "Acceptable Use Policy",
    intro:
      "Rules that help keep the nartvmora website and shopping services safe and available.",
    sections: [
      {
        id: "lawful-use",
        title: "Lawful and fair use",
        body: [
          "Use the website only for lawful personal shopping, customer service and other purposes we make available. Do not use it in a way that violates another person's rights or applicable law.",
        ],
      },
      {
        id: "prohibited",
        title: "Prohibited activity",
        items: [
          "Accessing another person's order, account, payment method or personal information without authority.",
          "Introducing malware, attempting to bypass security, probing vulnerabilities or disrupting service availability.",
          "Using bots, scraping, data mining or automated purchasing in a way that burdens the service, evades purchase limits or commercially exploits content without permission.",
          "Submitting false, fraudulent, threatening, abusive, defamatory, discriminatory, obscene or infringing material.",
          "Impersonating nartvmora, its personnel or another person; using the site for phishing, counterfeiting or unauthorised resale.",
          "Manipulating promotions, returns, reviews, payment disputes or checkout to obtain an improper benefit.",
        ],
      },
      {
        id: "submitted-content",
        title: "Messages and submitted material",
        body: [
          "A person submitting text or images must have the right to do so and must avoid unnecessary personal, confidential or payment information. Customer-care submissions may be used only as reasonably needed to respond, investigate and administer the service, as described in the Privacy Notice.",
        ],
        links: [{ to: "/privacy", label: "Privacy notice" }],
      },
      {
        id: "enforcement",
        title: "Enforcement",
        body: [
          "We may limit an order, block abusive traffic, preserve relevant records or report suspected unlawful conduct when reasonably necessary and lawful. Where appropriate, we will consider context, severity and whether a less restrictive response is available.",
          "Nothing in this policy prevents legitimate security research conducted with prior written authorisation or an activity protected by law.",
        ],
      },
      {
        id: "report",
        title: "Report misuse",
        body: [
          `Send suspected misuse to ${SUPPORT_EMAIL}. Include URLs, dates and a concise description, but do not forward malware, passwords or full payment-card details.`,
        ],
      },
    ],
  },

  accessibility: {
    eyebrow: "Accessibility",
    title: "Accessibility Statement",
    intro:
      "Our aim is to make nartvmora usable by as many people as reasonably possible.",
    sections: [
      {
        id: "commitment",
        title: "Our commitment",
        body: [
          "We work to provide clear navigation, readable content and keyboard-accessible shopping interactions, and to improve compatibility with commonly used browsers and assistive technologies.",
          "Accessibility is an ongoing process. This statement describes an intention and feedback route; it is not a claim that every page or third-party service is free of barriers.",
        ],
      },
      {
        id: "measures",
        title: "Measures used on this storefront",
        items: [
          "Semantic headings, landmarks, labels and descriptive link or button names.",
          "Visible keyboard focus and controls that can be reached without a pointer where supported by the interface.",
          "Text alternatives for informative images and empty alternatives for decorative images.",
          "Responsive layouts, readable contrast and form errors associated with the relevant fields.",
          "Reduced-motion behaviour where the visitor's operating system expresses that preference.",
        ],
      },
      {
        id: "third-party",
        title: "Third-party content and payment",
        body: [
          "Some parts of the shopping journey, including the hosted payment page, are provided by third parties. We do not control every aspect of those services, but accessibility feedback about the complete journey is welcome so the issue can be investigated or raised with the provider.",
        ],
      },
      {
        id: "feedback",
        title: "Accessibility help and feedback",
        body: [
          `If a page is difficult to use, email ${SUPPORT_EMAIL} with the page address, the task you were trying to complete, the browser or assistive technology used, and a description of the barrier. Please do not include sensitive payment information.`,
          "We will try to provide the information or service in a reasonable alternative format and will use the report to prioritise improvements.",
        ],
        links: [{ to: "/contact", label: "Request accessibility help" }],
      },
      {
        id: "review",
        title: "Review of this statement",
        body: [
          "This statement is reviewed when significant storefront or checkout changes are made. The date above shows the latest review of the published statement.",
        ],
      },
    ],
  },

  intellectualProperty: {
    eyebrow: "Legal",
    title: "Intellectual Property & Takedown Policy",
    intro:
      "Rules for nartvmora content and a process for reporting material that may infringe another person's rights.",
    sections: [
      {
        id: "site-content",
        title: "Site content",
        body: [
          "The nartvmora name, original site text, product names, photographs, graphics, layout and software are owned by or licensed to the operator of nartvmora and may be protected by copyright, trade mark, design and other laws.",
          "Visitors may view the site and retain ordinary order records for personal use. No other licence is granted. Content may not be copied, republished, sold, framed, altered or used to train a commercial system without permission or another lawful basis.",
        ],
      },
      {
        id: "third-party-rights",
        title: "Third-party rights",
        body: [
          "Third-party names, payment marks and services remain the property of their respective owners. Their appearance identifies compatibility or a service relationship and does not transfer rights or imply an endorsement beyond that relationship.",
        ],
      },
      {
        id: "reporting",
        title: "Report potentially infringing material",
        body: [
          `Send a notice to ${SUPPORT_EMAIL} with the subject “IP Notice”. Identify the protected work or right, the exact website URL or product concerned, why the use is unauthorised, and reliable contact details. Include a statement that the report is accurate and made in good faith.`,
          "Do not send more personal information than necessary. Knowingly false or misleading reports may cause harm and may have legal consequences.",
        ],
      },
      {
        id: "review-response",
        title: "Review and response",
        body: [
          "We may request supporting evidence, remove or restrict material while a report is reviewed, notify the person responsible for the material, or decline a report that lacks enough information. Affected parties may be invited to provide a response where appropriate.",
          "Removal under this policy does not decide ownership or liability and does not waive any right or remedy.",
        ],
      },
      {
        id: "counterfeit",
        title: "Counterfeit and impersonation reports",
        body: [
          "Reports about a website, social account or product impersonating nartvmora should include the relevant URL, screenshots and any order or payment reference. Do not include account passwords or full card details.",
        ],
        links: [
          { to: "/acceptable-use", label: "Acceptable use policy" },
          { to: "/contact", label: "Contact us" },
        ],
      },
    ],
  },
};

function AdditionalPolicy({ type }) {
  const policy = policies[type] || policies.hub;

  return (
    <PolicyLayout
      eyebrow={policy.eyebrow}
      title={policy.title}
      intro={policy.intro}
      updated={policy.updated || EFFECTIVE_DATE}
      sections={policy.sections}
      action={{ to: "/contact", label: "Contact customer care" }}
    />
  );
}

export default AdditionalPolicy;
